/***
 * Summary: here resides the scheme and roles for the fields of the network performing
 * 
 */
import { yupToFormErrors } from "formik";
import * as Yup from "yup";

const TODIS_NFREIGHTERMINAL_SCHEMA = Yup.object().shape({
    //general section Rules:
    dateValidTo: Yup.date("Valid from is a Date").required().nullable(true),
    dateValidFrom: Yup.date("Valid to is a Date").required().nullable(true),
    dateValidFor: Yup.string("Valid for is a Year"),
    tenTCategory: Yup.string("tent category is alphanumeric").nullable(true),
    nodeName: Yup.string("Road ID is alphanumeric").nullable(true),
    nameResp: Yup.string("Road ID is alphanumeric").nullable(true),
    addressResp: Yup.string("Road ID is alphanumeric").nullable(true),
    contactPer: Yup.string("Road ID is alphanumeric").nullable(true),
    contactPos: Yup.string("Road ID is alphanumeric").nullable(true),
    contactNum: Yup.string()
        .transform((originalValue) => {
            // Si el valor comienza con '+', eliminamos ese símbolo
            return typeof originalValue == 'string' ? originalValue.replace(/^\+/, '') : originalValue;
        })
        .nullable(true)
        .test('is-valid', 'Invalid data. \'Phone Number\' must be a valid number', function (value) {
            // Permitir números, espacios, y la presencia de '+'
            const isValid = /^[0-9+\s]+$/.test(value) || value == null || value == undefined;

            if (!isValid) {
                throw this.createError({ message: 'Invalid data. \'Phone Number\' must be a valid number' });
            }

            return true;
        }),

    contactEmail: Yup.string("Email")
        .transform((originalValue) => {
            // Realizar transformaciones si es necesario
            return typeof originalValue == 'string' ? originalValue.trim() : originalValue;
        })
        .nullable(true)
        .test('is-email', 'Invalid data. \'Email\' must be valid', function (value) {
            // Si el valor es null, lo consideramos válido y no aplicamos más validaciones
            if (value == null) {
                return true;
            }

            const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
            const emails = value.split(/[,;:]+/);  // Separar por comas, punto y coma, o dos puntos

            for (const email of emails) {
                if (!emailRegex.test(email.trim())) {
                    throw this.createError({ message: `Invalid data. '${email}' must be a valid email address` });
                }
            }

            return true;
        }),

    regionalPartner: Yup.string("Regional partner").nullable(true),
    ownershipType: Yup.string().nullable(true),
    owner1: Yup.string().nullable(true),
    ownershipType: Yup.string().nullable(true),
    owner1: Yup.string().nullable(true),
    owner1Per: Yup.number()
        .required("Majority Ownership Porcentage is required")
        .min(0, "Main ownership Porcentage must be greater than or equal to 0")
        .max(100, "Main ownership Porcentage must be less than or equal to 100")
        .test('total-percentage', 'Sum of ownership percentages should be 100%', function (value) {
            const owner1Per = parseFloat(this.parent.owner1Per) || 0;
            const ownerOtherPer = parseFloat(this.parent.ownerOtherPer) || 0;
            return owner1Per + ownerOtherPer === 100;
        }),
    ownerOther: Yup.string()
        .test('ownership-format', 'Invalid ownership format', function (value) {
            // Verificar el formato owner;porcentaje;owner;porcentaje
            if (!value) return true; // Permitir valor vacío
            const pairs = value.split(';');
            // Debe haber un número par de elementos
            if (pairs.length % 2 !== 0) return false;
            // Comprobar si cada par tiene un owner y un porcentaje válido
            for (let i = 0; i < pairs.length; i += 2) {
                const owner = pairs[i].trim();
                const percentage = parseFloat(pairs[i + 1].trim());
                if (!owner || isNaN(percentage) || percentage < 0 || percentage > 100) {
                    return false;
                }
            }
            return true;
        })
        .nullable(true),
    ownerOtherPer: Yup.number()
        .when('ownerOther', {
            is: value => value && value.trim().length > 0,
            then: Yup.number()
                .required('The percentage of secondary owners must be filled in if the "Secondary Owners" field is filled in.')
                .test('sum-of-percentages', 'Sum of secondary owners should match', function (value) {
                    const pairs = this.parent.ownerOther ? this.parent.ownerOther.split(';') : [];
                    let totalPercentage = 0;
                    for (let i = 1; i < pairs.length; i += 2) {
                        totalPercentage += parseFloat(pairs[i].trim());
                    }
                    return totalPercentage === value;
                })
        })
        .nullable(true),

    remarks: Yup.string("remarks").nullable(true),

    freightNpmEnvDTO: Yup.object().shape({
        co2Ems: Yup.number()
            .typeError("Invalid data. 'CO2 emissions (tonnes/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'CO2 emissions (tonnes/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'CO2 emissions (tonnes/year)\' must be a number' });
                }
                return true;
            }),
        noxEms: Yup.number()
            .typeError("Invalid data. 'NOx emissions (tonnes/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'NOx emissions (tonnes/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'NOx emissions (tonnes/year)\' must be a number' });
                }
                return true;
            }),
        so2Ems: Yup.number()
            .typeError("Invalid data. 'SO2 emissions (tonnes/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'SO2 emissions (tonnes/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'SO2 emissions (tonnes/year)\' must be a number' });
                }
                return true;
            }),
        nmhcEms: Yup.number()
            .typeError("Invalid data. 'Non-methane hydrocarbons (tonnes/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Non-methane hydrocarbons (tonnes/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Non-methane hydrocarbons (tonnes/year)\' must be a number' });
                }
                return true;
            }),
        ppm: Yup.number()
            .typeError("Invalid data. 'Particulate matter (ppm)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Particulate matter (ppm)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Particulate matter (ppm)\' must be a number' });
                }
                return true;
            }),
        chFloodInci: Yup.number()
            .typeError("Invalid data. 'Flood' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Flood\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Flood\' must be a number' });
                }
                return true;
            }),
        chClosureAdvWeatherCond: Yup.number()
            .typeError("Invalid data. 'Closures due to adverse weather conditions (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Closures due to adverse weather conditions (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Closures due to adverse weather conditions (no.)\' must be a number' });
                }
                return true;
            }),
        chEmbankFail: Yup.number()
            .typeError("Invalid data. 'Embankment failures (no.)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Embankment failures (no.)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Embankment failures (no.)\' must be a number' });
                }
                return true;
            }),
        remarks: Yup.string("remarks").nullable(true),
    }),
    freightNpmInfraDTO: Yup.object().shape({
        condition: Yup.string('Value should be a text').nullable(true),
        totalArea: Yup.string('Value should be a text').nullable(true),
        transhipEquip: Yup.string('Value should be a text').nullable(true),
        transhipFaciliIntermodTrans: Yup.boolean().nullable(true),
        railConn: Yup.boolean().nullable(true),
        railConnTracks: Yup.number()
            .typeError("Invalid data. 'Rail connection - no. of tracks' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Rail connection - no. of tracks\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Rail connection - no. of tracks\' must be a number' });
                }
                return true;
            }),
        traction: Yup.number()
            .typeError("Invalid data. 'Rail connection – traction' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Rail connection – traction\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Rail connection – traction\' must be a number' });
                }
                return true;
            }),
        loadGauge: Yup.string('Value should be a text').nullable(true),
        maxAxleLoad: Yup.number()
            .typeError("Invalid data. 'Rail connection – max. axle load' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Rail connection – max. axle load\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Rail connection – max. axle load\' must be a number' });
                }
                return true;
            }),
        roadConn: Yup.boolean().nullable(true),
        roadConnLanes: Yup.number()
            .typeError("Invalid data. 'Road Connection Number' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Road Connection Number\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Road Connection Number\' must be a number' });
                }
                return true;
            }),
        iwwConn: Yup.boolean().nullable(true),
        seaConn: Yup.boolean().nullable(true),
        airConn: Yup.boolean().nullable(true),
        cleanFuelAvail: Yup.boolean().nullable(true),
        freightCapacityTpy: Yup.number()
            .typeError("Invalid data. 'Freight capacity (terminal maximum cargo handling capacity – tonnes/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Freight capacity (terminal maximum cargo handling capacity – tonnes/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Freight capacity (terminal maximum cargo handling capacity – tonnes/year)\' must be a number' });
                }
                return true;
            }),
        openStorage: Yup.number()
            .typeError("Invalid data. 'Open storage (m2)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Open storage (m2)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Open storage (m2)\' must be a number' });
                }
                return true;
            }),
        silosCapacity: Yup.number()
            .typeError("Invalid data. 'Silos capacity (m3)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Silos capacity (m3)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Silos capacity (m3)\' must be a number' });
                }
                return true;
            }),
        stackArea: Yup.number()
            .typeError("Invalid data. 'Stack area (m2)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Stack area (m2)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Stack area (m2)\' must be a number' });
                }
                return true;
            }),
        tanksCapacity: Yup.number()
            .typeError("Invalid data. 'Tanks Capacity (m3)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Tanks Capacity (m3)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Tanks Capacity (m3)\' must be a number' });
                }
                return true;
            }),
        warehouseCapacity: Yup.number()
            .typeError("Invalid data. 'Warehouse Capacity (m3)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Warehouse Capacity (m3)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Warehouse Capacity (m3)\' must be a number' });
                }
                return true;
            }),
        reeferCapacity: Yup.number()
            .typeError("Invalid data. 'Reefer Capacity (m3)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Reefer Capacity (m3)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Reefer Capacity (m3)\' must be a number' });
                }
                return true;
            }),
        fridgeCapacity: Yup.number()
            .typeError("Invalid data. 'Fridge Capacity (m3)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Fridge Capacity (m3)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Fridge Capacity (m3)\' must be a number' });
                }
                return true;
            }),
        hazardGoodsCapacity: Yup.number()
            .typeError("Invalid data. 'Hazardous goods Capacity (m3)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Hazardous goods Capacity (m3)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Hazardous goods Capacity (m3)\' must be a number' });
                }
                return true;
            }),
        its: Yup.boolean().nullable(true),
        itsType: Yup.string('Value should be a text').nullable(true),
        rechargingStation: Yup.boolean().nullable(true),
        hydrogenRefuellingStation: Yup.boolean().nullable(true),
        marketProspectiveAnalysis: Yup.boolean().nullable(true),
        remarks: Yup.string("remarks").nullable(true),
    }),
    freightNpmMaintDTO: Yup.object().shape({
        maintCostTotalPy: Yup.number()
            .typeError("Invalid data. 'Maintenance cost – total (Euro/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Maintenance cost – total (Euro/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Maintenance cost – total (Euro/year)\' must be a number' });
                }
                return true;
            }),
        emergencyCostPy: Yup.number()
            .typeError("Invalid data. 'Emergency Maintenance Cost (Euro/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Emergency Maintenance Cost (Euro/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Emergency Maintenance Cost (Euro/year)\' must be a number' });
                }
                return true;
            }),
        routineCostPy: Yup.number()
            .typeError("Invalid data. 'Emergency Maintenance Cost (Euro/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Emergency Maintenance Cost (Euro/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Emergency Maintenance Cost (Euro/year)\' must be a number' });
                }
                return true;
            }),
        financeSource: Yup.string("Source of finance").nullable(true),
        reqUpgradeIncCap: Yup.boolean().nullable(true),
        remarks: Yup.string("remarks").nullable(true),
    }),
    freightNpmOpsDTO: Yup.object().shape({
        terminalTrafficVeh: Yup.number()
            .typeError("Invalid data. 'Terminal traffic (vehicles/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Terminal traffic (vehicles/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Terminal traffic (vehicles/year)\' must be a number' });
                }
                return true;
            }),
        terminalTrafficTrains: Yup.number()
            .typeError("Invalid data. 'Terminal traffic (trains/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Terminal traffic (trains/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Terminal traffic (trains/year)\' must be a number' });
                }
                return true;
            }),
        rminalTrafficVessels: Yup.number()
            .typeError("Invalid data. 'Terminal traffic (vessels/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Terminal traffic (vessels/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Terminal traffic (vessels/year)\' must be a number' });
                }
                return true;
            }),
        rminalTrafficAircrafts: Yup.number()
            .typeError("Invalid data. 'Terminal traffic (aircrafts/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Terminal traffic (aircrafts/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Terminal traffic (aircrafts/year)\' must be a number' });
                }
                return true;
            }),
        freightTrafficTonsPy: Yup.number()
            .typeError("Invalid data. 'Freigh traffic – total (tonnes/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Freigh traffic – total (tonnes/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Freigh traffic – total (tonnes/year)\' must be a number' });
                }
                return true;
            }),
        freightTrafficTonsLoaded: Yup.number()
            .typeError("Invalid data. 'Freight traffic – tonnes loaded (kT/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Freight traffic – tonnes loaded (kT/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Freight traffic – tonnes loaded (kT/year)\' must be a number' });
                }
                return true;
            }),
        freightTrafficTonsUnloaded: Yup.number()
            .typeError("Invalid data. 'Freight traffic – tonnes unloaded (kT/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Freight traffic – tonnes unloaded (kT/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Freight traffic – tonnes unloaded (kT/year)\' must be a number' });
                }
                return true;
            }),
        teuTons: Yup.number()
            .typeError("Invalid data. 'TEU tonnes (kT/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'TEU tonnes (kT/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'TEU tonnes (kT/year)\' must be a number' });
                }
                return true;
            }),
        teusContainersPy: Yup.number()
            .typeError("Invalid data. 'TEU containers (no./year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'TEU containers (no./year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'TEU containers (no./year)\' must be a number' });
                }
                return true;
            }),
        roro: Yup.number()
            .typeError("Invalid data. 'RoRo (kT/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'RoRo (kT/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'RoRo (kT/year)\' must be a number' });
                }
                return true;
            }),
        rorosVeh: Yup.number()
            .typeError("Invalid data. 'RoRos (no. of vehicles/year)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'RoRos (no. of vehicles/year)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'RoRos (no. of vehicles/year)\' must be a number' });
                }
                return true;
            }),
        domesticTraffic: Yup.number()
            .typeError("Invalid data. 'Domestic traffic (% of TEU tonnes)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Domestic traffic (% of TEU tonnes)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Domestic traffic (% of TEU tonnes)\' must be a number' });
                }
                return true;
            }),
        transportCommTraffic: Yup.number()
            .typeError("Invalid data. 'Transport Community Traffic (% of TEU tonnes)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Transport Community Traffic (% of TEU tonnes)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Transport Community Traffic (% of TEU tonnes)\' must be a number' });
                }
                return true;
            }),
        euTraffic: Yup.number()
            .typeError("Invalid data. 'EU traffic (% of TEU tonnes)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'EU traffic (% of TEU tonnes)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'EU traffic (% of TEU tonnes)\' must be a number' });
                }
                return true;
            }),
        containersTrafficOrigin: Yup.number()
            .typeError("Invalid data. 'Containers traffic by origin (TEU tonnes)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Containers traffic by origin (TEU tonnes)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Containers traffic by origin (TEU tonnes)\' must be a number' });
                }
                return true;
            }),
        containersTrafficDest: Yup.number()
            .typeError("Invalid data. 'Containers traffic by destination (TEU tonnes)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Containers traffic by destination (TEU tonnes)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Containers traffic by destination (TEU tonnes)\' must be a number' });
                }
                return true;
            }),
        storageCapacityUsed: Yup.number()
            .typeError("Invalid data. 'Storage capacity used (% of capacity)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Storage capacity used (% of capacity)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Storage capacity used (% of capacity)\' must be a number' });
                }
                return true;
            }),
        trsnshipmentCapacityUsed: Yup.number()
            .typeError("Invalid data. 'Transhipment capacity used (% of capacity)' must be a 'number'")
            .nullable(true)
            .test('is-number', 'Invalid data. \'Transhipment capacity used (% of capacity)\' must be a number', function (value) {
                if (value === null && value === undefined && (isNaN(value) || value !== 0)) {
                    throw this.createError({ message: 'Invalid data. \'Transhipment capacity used (% of capacity)\' must be a number' });
                }
                return true;
            }),
        remarks: Yup.string("remarks").nullable(true)
    }),
    freightNpmTentcompDTO: Yup.object().shape({
        interModality: Yup.boolean().nullable(true),
        trainLength740M: Yup.boolean().nullable(true),
        electrifiedAccess: Yup.boolean().nullable(true),
        openAvailability: Yup.boolean().nullable(true),
        connectionTwoModesTransport: Yup.string().nullable(true),
        rechargingStation: Yup.string().nullable(true),
        hydrogenRefuellingStation: Yup.string().nullable(true),
        marketProspectiveAnalysis: Yup.string().nullable(true),
        remarks: Yup.string("remarks").nullable(true)
    })

});

const NFREIGHTERMINAL_INIT = {
    //general section Rules:
    dateValidTo: "",
    dateValidFrom: "",
    dateValidFor: "",
    tenTCategory: "",
    nodeName: "",
    nameResp: "",
    addressResp: "",
    contactPer: "",
    contactPos: "",
    contactNum: 0,
    contactEmail: "",
    regionalPartner: "",
    ownershipType: "",
    owner1: "",
    owner1Per: "",
    ownerOther: "",
    ownerOtherPer: "",
    remarks: "",
    freightNpmEnvDTO: {
        airPollutGhgTpy: "",
        co2Ems: "",
        noxEms: "",
        so2Ems: "",
        nmhcEms: "",
        ppm: "",
        noise: "",
        chFloodInci: "",
        chClosureAdvWeatherCond: "",
        chEmbankFail: "",
        chWinterMaintDays: "",
        remarks: "",
    },
    freightNpmInfraDTO: {
        condition: "",
        totalArea: "",
        transhipEquip: "",
        transhipFaciliIntermodTrans: false,
        railConn: false,
        railConnTracks: "",
        traction: "",
        loadGauge: "",
        maxAxleLoad: "",
        roadConn: false,
        roadConnLanes: "",
        iwwConn: false,
        seaConn: false,
        airConn: false,
        cleanFuelAvail: false,
        freightCapacityTpy: "",
        openStorage: "",
        silosCapacity: "",
        stackArea: "",
        tanksCapacity: "",
        warehouseCapacity: "",
        reeferCapacity: "",
        fridgeCapacity: "",
        hazardGoodsCapacity: "",
        its: false,
        rechargingStation: false,
        hydrogenRefuellingStation: false,
        marketProspectiveAnalysis: false,
        itsType: "",
        remarks: "",
    },
    freightNpmMaintDTO: {
        maintCostTotalPy: "",
        emergencyCostPy: "",
        routineCostPy: "",
        financeSource: "",
        reqUpgradeIncCap: false,
        remarks: "",
    },
    freightNpmOpsDTO: {
        terminalTrafficVeh: "",
        terminalTrafficTrains: "",
        rminalTrafficVessels: "",
        rminalTrafficAircrafts: "",
        freightTrafficTonsPy: "",
        freightTrafficTonsLoaded: "",
        freightTrafficTonsUnloaded: "",
        teuTons: "",
        teusContainersPy: "",
        roro: "",
        rorosVeh: "",
        domesticTraffic: "",
        transportCommTraffic: "",
        euTraffic: "",
        containersTrafficOrigin: "",
        containersTrafficDest: "",
        storageCapacityUsed: "",
        trsnshipmentCapacityUsed: "",
        remarks: "",
    },
    freightNpmTentcompDTO: {
        interModality: false,
        trainLength740M: false,
        electrifiedAccess: false,
        openAvailability: false,
        connectionTwoModesTransport: "",
        rechargingStation: "",
        hydrogenRefuellingStation: "",
        marketProspectiveAnalysis: "",
        remarks: "",
    }
}

export { TODIS_NFREIGHTERMINAL_SCHEMA, NFREIGHTERMINAL_INIT };